import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { tripService } from '../../services/tripService';

import { inventoryService } from '../../services/inventoryService';
import Header from '../../components/navigation/Header';
import MobileNavigation from '../../components/navigation/MobileNavigation';
import TripStatusCard from './components/TripStatusCard';
import InventoryStatusPanel from './components/InventoryStatusPanel';
import UpgradeModal from '../../components/ui/UpgradeModal';
import { AlertCircle } from 'lucide-react';

const TripDashboard = () => {
  const navigate = useNavigate();
  const { user, userProfile } = useAuth();
  const [trip, setTrip] = useState(null);
  const [inventoryItems, setInventoryItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [dataWasCleanedUp, setDataWasCleanedUp] = useState(false);

  useEffect(() => {
    const loadDashboardData = async () => {
      if (!user?.id) return;

      try {
        const [tripResult, inventoryResult] = await Promise.all([
          tripService?.getActiveTrip(user?.id),
          inventoryService?.getInventoryItems(user?.id)
        ]);

        if (tripResult?.data) {
          setTrip(tripResult?.data);
          setInventoryItems(inventoryResult?.data || []);
          setDataWasCleanedUp(false);
        } else if (!tripResult?.data) {
          // Check if this is due to trial cleanup
          if (userProfile?.subscription_status === 'trial' && !userProfile?.trial_start_date) {
            setDataWasCleanedUp(true);
          }
          setTrip(null);
        }

        setLoading(false);
      } catch (error) {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [user, navigate, userProfile]);

  if (loading) {
    return (
      <>
        <Helmet>
          <title>Dashboard - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center">
          <div className="text-center">
            <p className="text-lg text-muted-foreground">Loading dashboard...</p>
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  if (!trip) {
    return (
      <>
        <Helmet>
          <title>Dashboard - GalleyPlan</title>
        </Helmet>
        <Header />
        <div className="min-h-screen pt-16 flex items-center justify-center px-4">
          <div className="text-center max-w-md">
            {dataWasCleanedUp ? (
              <>
                <div className="mb-4 flex justify-center">
                  <AlertCircle className="w-16 h-16 text-blue-500" />
                </div>
                <h2 className="text-2xl font-semibold text-foreground mb-3">7-Day Trial Completed</h2>
                <p className="text-muted-foreground mb-4">
                  Your trial period has ended and your trip data has been cleared. You can continue using GalleyPlan by creating a new trip for another 7-day cycle.
                </p>
                <p className="text-sm text-muted-foreground mb-6">
                  Want unlimited access? Upgrade to Premium for trips up to 60 days!
                </p>
                <div className="flex flex-col sm:flex-row gap-3 justify-center">
                  <button
                    onClick={() => navigate('/trip-setup')}
                    className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                  >
                    Start New Trip
                  </button>
                  <button
                    onClick={() => setShowUpgradeModal(true)}
                    className="px-6 py-3 bg-secondary text-secondary-foreground rounded-lg hover:bg-secondary/80 transition-colors font-medium"
                  >
                    Upgrade to Premium
                  </button>
                </div>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-semibold text-foreground mb-2">No Active Trip</h2>
                <p className="text-muted-foreground mb-6">Create a trip to get started</p>
                <button
                  onClick={() => navigate('/trip-setup')}
                  className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors font-medium"
                >
                  Create Trip
                </button>
              </>
            )}
          </div>
        </div>
        <MobileNavigation />
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Dashboard - GalleyPlan</title>
        <meta 
          name="description" 
          content="View your trip overview, inventory status, and provisioning focus for your coastal cruise" 
        />
      </Helmet>
      <Header />
      <div className="min-h-screen bg-background pt-16 pb-20 lg:pb-8">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
          <TripStatusCard trip={trip} onTripUpdate={() => {}} />
          
          <div className="mt-6">
            <InventoryStatusPanel inventoryStatus={inventoryItems} />
          </div>
        </div>
      </div>
      <UpgradeModal 
        isOpen={showUpgradeModal} 
        onClose={() => setShowUpgradeModal(false)} 
      />

      <MobileNavigation />
    </>
  );
};

export default TripDashboard;